import type {UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {extractImageFilesFromClipboard, PLAIN_TEXT_CLIPBOARD_MIMETYPE} from '@Utils/clipboard.util';
import type {ClipboardEvent} from 'react';
import type {ItemObject, SlideItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {hasImageShapeModifications} from '@Libraries/image-item.library';
import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {ImageSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import type {TextSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/text-slide-item.class';

type AttachImagesCallback = (attachments: UserImageAttachment[]) => void;

interface PosterItemsPasteOptions {
  pasteAsReferenceImages: boolean;
  onReferenceImageFiles: (files: File[]) => void;
  onAttachImages?: AttachImagesCallback;
  onPasteText: (text: string) => void;
}

const PASTED_TEXT_ITEMS_SEPARATOR = '\n';

export const handlePosterItemsPaste = (event: ClipboardEvent, options: PosterItemsPasteOptions): boolean => {
  const clipboardText = event.clipboardData?.getData(PLAIN_TEXT_CLIPBOARD_MIMETYPE);
  if (!clipboardText) {
    return false;
  }
  const posterItems = getClipboardPosterItems(clipboardText);
  if (posterItems === null) {
    return false;
  }

  event.preventDefault();
  const pastedText = getTextOfTextItems(posterItems);
  if (pastedText) {
    options.onPasteText(pastedText);
  }
  const imageItems = posterItems.flatMap(extractImageItemObjects).filter(isUploadedImageItem);
  if (imageItems.length > 0) {
    attachPastedImageItems(event, imageItems, options);
  } else if (options.pasteAsReferenceImages && posterItems.some(isRenderedOnCopy)) {
    attachPastedRenderedItem(event, options);
  }
  return true;
};

const getTextOfTextItems = (items: ItemObject[]): string => {
  return items
    .map(getTextOfItem)
    .map((text) => text.trim())
    .filter((text) => text !== '')
    .join(PASTED_TEXT_ITEMS_SEPARATOR);
};

const isPastedAsText = (item: ItemObject): boolean => {
  return isTextItemObject(item) || isTextSlideItemObject(getRepresentativeSlideObject(item));
};

const getTextOfItem = (item: ItemObject): string => {
  if (isTextItemObject(item)) {
    return item.text;
  }
  const slide = getRepresentativeSlideObject(item);
  return isTextSlideItemObject(slide) ? slide.text : '';
};

const getRepresentativeSlideObject = (item: ItemObject): SlideItemObject | undefined => {
  if (!isSlideshowItemObject(item)) {
    return undefined;
  }
  return item.slides.slidesHashMap[item.selectedSlideUID] ?? item.slides.slidesHashMap[item.slides.slidesOrder[0]];
};

const attachPastedImageItems = (event: ClipboardEvent, imageItems: ImageItemObject[], options: PosterItemsPasteOptions): void => {
  const isRenderedCopyAttached = !imageItems[0].isBackground && options.pasteAsReferenceImages && attachPastedRenderedItem(event, options);
  const existingUploads = isRenderedCopyAttached ? imageItems.filter((imageItem) => !hasImageShapeModifications(imageItem)) : imageItems;
  if (existingUploads.length > 0) {
    options.onAttachImages?.(existingUploads.map(toImageAttachment));
  }
};

const attachPastedRenderedItem = (event: ClipboardEvent, options: PosterItemsPasteOptions): boolean => {
  const pastedImageFiles = extractImageFilesFromClipboard(event.clipboardData?.items);
  if (pastedImageFiles.length === 0) {
    return false;
  }
  options.onReferenceImageFiles([pastedImageFiles[0]]);
  return true;
};

const getClipboardPosterItems = (text: string): ItemObject[] | null => {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  const graphicItemObjects = extractGraphicItemObjects(parsed);
  if (graphicItemObjects === null) {
    return null;
  }
  const backgroundItem = extractBackgroundItemObject(parsed);
  return backgroundItem ? [...graphicItemObjects, backgroundItem] : graphicItemObjects;
};

const isTextItemObject = (item: ItemObject): item is TextItemObject => {
  return item.gitype === ITEM_TYPE.TEXT;
};

const isTextSlideItemObject = (slide: SlideItemObject | undefined): slide is TextSlideItemObject => {
  return slide?.gitype === ITEM_TYPE.TEXTSLIDE;
};

const isRenderedOnCopy = (item: ItemObject): boolean => {
  return !isPastedAsText(item) && !item.isBackground;
};

const extractBackgroundItemObject = (parsed: unknown): ItemObject | null => {
  if (!parsed || typeof parsed !== 'object' || !('backgroundItemObject' in parsed)) {
    return null;
  }
  const background = parsed.backgroundItemObject;
  return background && typeof background === 'object' ? (background as ItemObject) : null;
};

const extractImageItemObjects = (item: ItemObject): ImageItemObject[] => {
  if (isImageItemObject(item)) {
    return [item];
  }
  if (isSlideshowItemObject(item)) {
    const imageSlides = item.slides.slidesOrder.map((slideId) => item.slides.slidesHashMap[slideId]).filter(isImageSlideItemObject);
    const selectedSlide = imageSlides.find((slide) => slide.uid === item.selectedSlideUID);
    return selectedSlide ? [selectedSlide, ...imageSlides.filter((slide) => slide !== selectedSlide)] : imageSlides;
  }
  return [];
};

const extractGraphicItemObjects = (parsed: unknown): ItemObject[] | null => {
  if (!parsed || typeof parsed !== 'object' || !('graphicItemObjects' in parsed)) {
    return null;
  }
  const items = parsed.graphicItemObjects;
  if (!Array.isArray(items)) {
    return null;
  }
  return items.filter((item): item is ItemObject => typeof item === 'object' && item !== null);
};

export const isImageItemObject = (item: ItemObject): item is ImageItemObject => {
  return item.gitype === ITEM_TYPE.IMAGE;
};

export const isSlideshowItemObject = (item: ItemObject): item is SlideshowItemObject => {
  return item.gitype === ITEM_TYPE.SLIDESHOW;
};

export const isImageSlideItemObject = (item: ItemObject): item is ImageSlideItemObject => {
  return item.gitype === ITEM_TYPE.IMAGESLIDE;
};

export const isImageBackgroundItemObject = (item: ItemObject): item is ImageItemObject => {
  return item.gitype === ITEM_TYPE.IMAGE && item.isBackground;
};

export const isUploadedImageItem = (item: ImageItemObject): boolean => {
  return Boolean(item.hashedFilename) && Boolean(item.fileExtension);
};

const toImageAttachment = (item: ImageItemObject): UserImageAttachment => {
  return {
    type: PromptAttachmentType.IMAGE,
    hashedFilename: item.hashedFilename ?? '',
    extension: item.fileExtension ?? '',
    source: item.imageSource,
    width: item.loadedImageWidth,
    height: item.loadedImageHeight,
  };
};
